// Offline Capture Store — IndexedDB schema + connection helpers.
//
// Owns the singleton database connection used by the public API. The schema
// is created via `onupgradeneeded` and is intentionally simple:
//
//   - `sessions`   keyPath: "sessionId"            (full session JSON blob)
//   - `ops_log`    keyPath: "id" autoIncrement     (WAL; indexes: sessionId, clientOpId)
//   - `snapshots`  keyPath: "id" autoIncrement     (per-session history; index: sessionId)
//
// Connections are cached per database name so repeated calls to
// `openDatabase()` reuse the same IDBDatabase instance.

export const DB_NAME = "aid_erp_offline_capture";
export const DB_VERSION = 1;

export const STORE_SESSIONS = "sessions";
export const STORE_OPS_LOG = "ops_log";
export const STORE_SNAPSHOTS = "snapshots";

const connections = new Map<string, Promise<IDBDatabase>>();

/**
 * Open (or reuse) a connection to the offline capture database. `dbName` is
 * exposed as an argument so tests can isolate themselves on a separate
 * database without conflicting with production storage.
 */
export function openDatabase(dbName: string = DB_NAME): Promise<IDBDatabase> {
  const cached = connections.get(dbName);
  if (cached) return cached;

  const promise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(dbName, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(STORE_SESSIONS)) {
        db.createObjectStore(STORE_SESSIONS, { keyPath: "sessionId" });
      }

      if (!db.objectStoreNames.contains(STORE_OPS_LOG)) {
        const opsStore = db.createObjectStore(STORE_OPS_LOG, {
          keyPath: "id",
          autoIncrement: true,
        });
        opsStore.createIndex("sessionId", "sessionId", { unique: false });
        opsStore.createIndex("clientOpId", "clientOpId", { unique: true });
      }

      if (!db.objectStoreNames.contains(STORE_SNAPSHOTS)) {
        const snapsStore = db.createObjectStore(STORE_SNAPSHOTS, {
          keyPath: "id",
          autoIncrement: true,
        });
        snapsStore.createIndex("sessionId", "sessionId", { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  connections.set(dbName, promise);
  return promise;
}

/**
 * Drop the cached connection for `dbName` (used by tests after wiping
 * fake-indexeddb so the next `openDatabase()` call reopens fresh). Does NOT
 * delete data on disk — only forgets the cached IDBDatabase handle.
 */
export function resetConnectionCache(dbName?: string): void {
  if (dbName === undefined) {
    connections.clear();
    return;
  }
  connections.delete(dbName);
}

/**
 * Promise wrapper around an IDBRequest. Resolves with the request's `result`
 * once `onsuccess` fires, rejects with `error` on failure.
 */
export function awaitRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Promise wrapper around an IDBTransaction lifecycle. Resolves on `complete`,
 * rejects on `error` or `abort`.
 */
export function awaitTransaction(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error("Transaction aborted"));
  });
}
